import * as React from 'react'
import { useState, useEffect } from 'react'
import { DemoContainer } from '@mui/x-date-pickers/internals/demo'
import { DateRange, LocalizationProvider, PickersShortcutsItem } from '@mui/x-date-pickers-pro'
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs'
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker'
import { Box } from '@mui/system'
import { TextField, Paper, Button, Autocomplete, AutocompleteRenderInputParams, Typography } from '@mui/material'
import { ThemeProvider } from '@mui/material/styles'
import { theme } from '../theme'
import { AccordionPage } from './AccordionPage/AccordionPage'
import { useTranslation } from 'react-i18next'
import i18n from '../Localization/localization'
import { getAllData, getAllPatient } from '../../API/api'
import dayjs from 'dayjs'
import { Load } from './AccordionPage/ControlledAccordions'
import useMediaQuery from '@mui/material/useMediaQuery'

export const EmptyLength = () => {
	const { t } = useTranslation()
	return (
		<>
			<Typography component={'h1'} sx={{ color: 'primary.main', fontSize: '25px' }}>
				{t('emptyData')}
			</Typography>
		</>
	)
}

function getDateRangeShortcuts(t: typeof i18n.t): PickersShortcutsItem<DateRange<dayjs.Dayjs>>[] {
	return [
		{
			label: t('daterange.this_day'),
			getValue: () => {
				const today = dayjs.utc()
				return [today.startOf('day'), today.endOf('day')]
			},
		},
		{
			label: t('daterange.this_week'),
			getValue: () => {
				const today = dayjs.utc()
				return [today.startOf('week'), today.endOf('week')]
			},
		},
		{
			label: t('daterange.last_week'),
			getValue: () => {
				const prevWeek = dayjs.utc().subtract(7, 'day')
				return [prevWeek.startOf('week'), prevWeek.endOf('week')]
			},
		},
		{
			label: t('daterange.last_7_days'),
			getValue: () => {
				const today = dayjs.utc()
				return [today.subtract(7, 'day'), today]
			},
		},
		{
			label: t('daterange.this_month'),
			getValue: () => {
				const today = dayjs.utc()
				return [today.startOf('month'), today.endOf('month')]
			},
		},
		{
			label: t('daterange.next_month'),
			getValue: () => {
				const startOfNextMonth = dayjs.utc().endOf('month').add(1, 'day')
				return [startOfNextMonth, startOfNextMonth.endOf('month')]
			},
		},
		{ label: t('daterange.reset'), getValue: () => [null, null] },
	]
}

let startFormat = dayjs().subtract(7, 'days').format('YYYY-MM-DD')
let endFormat = dayjs().subtract(0, 'days').format('YYYY-MM-DD')

export const Main = () => {
	const { t } = useTranslation()
	const [option, setOption] = useState<string[]>([])
	const [valueOption, setValueOption] = useState<any>()
	const [fetchData, setFetchData] = useState<any>([])
	const [idPatient, setIdPatient] = useState<string[]>()
	const [load, setLoad] = useState<boolean>(false)
	const [search, setSearch] = useState<boolean>(false)
	const [open, setOpen] = useState<string>('-')
	const isSmScreen = useMediaQuery('(max-width: 600px)')

	const openSearch = () => {
		if (search === true) {
			setSearch(false)
			setOpen('-')
		} else {
			setOpen('+')
			setSearch(true)
		}
	}
	let [starting, setStarting] = useState({
		start: startFormat,
		end: endFormat,
	})

	useEffect(() => {
		const fetchPostAll = async () => {
			let requestData = {
				data: {
					start: startFormat,
					end: endFormat,
				},
				id: '',
			}
			try {
				getAllData(requestData).then((data) => {
					if (data.error) {
						return
					}
					if (!data.data) return

					setFetchData(data)
				})
			} catch (error) {
				console.error('Error fetching data:', error)
			}
		}

		fetchPostAll()
	}, [])

	const [obj, setObj] = useState({
		start: '',
		end: '',
	})

	useEffect(() => {
		const fetchOption = async () => {
			try {
				const data = await getAllPatient()
				if (data.error) {
					return
				}
				if (!data.data) return
				const optionsId = data.data.map((e: any) => e.full_name)
				let patIdFull = data.data.map((i: any) => i)
				setOption(optionsId)
				setIdPatient(patIdFull)
			} catch (error) {
				console.error('Error fetching data:', error)
			}
		}
		fetchOption()
	}, [])

	let requestData = {
		data: obj,
		id: '',
	}
	const handleDateChange = (newDateRange: any) => {
		const startDate = newDateRange[0]
		const endDate = newDateRange[1]
		const formattedStartDate = dayjs(startDate).format('YYYY-MM-DD')
		const formattedEndDate = dayjs(endDate).format('YYYY-MM-DD')
		setObj({ start: startDate ? formattedStartDate : '', end: endDate ? formattedEndDate : '' })
		setStarting({ start: startDate ? formattedStartDate : '', end: endDate ? formattedEndDate : '' })
	}

	const changeOptionValue = (event: React.SyntheticEvent, value: { label: string } | null) => {
		if (value) {
			setValueOption({ name: value.label })
		} else {
			setValueOption({})
		}
	}
	const handleSubmit = (e: any) => {
		e.preventDefault()
		setLoad(true)
		idPatient?.map((e: any) => {
			if (valueOption !== undefined) {
				if (e.full_name === valueOption.name) {
					valueOption.name = valueOption.name + ' ' + e.id
				}
			}
			return undefined
		})
		if (valueOption) {
			let val = valueOption?.name ? valueOption.name.split(' ') : ''
			requestData.id = val[val.length - 1]
			requestData.data.start = starting.start
			requestData.data.end = starting.end
			if (requestData.id === undefined) {
				requestData.id = ''
			}
		}
		if (starting.start !== '' && starting.end !== '') {
			let val = valueOption?.name ? valueOption.name.split(' ') : ''
			requestData.data.start = starting.start
			requestData.data.end = starting.end
			requestData.id = val[val.length - 1]
			if (requestData.id === undefined) {
				requestData.id = ''
			}
		}

		const fetchPosts = async () => {
			try {
				getAllData(requestData).then((data) => {
					setLoad(true)
					if (data.error) {
						return
					}
					if (!data.data) return

					setLoad(false)
					setFetchData(data)
				})
			} catch (error) {
				console.error('Error fetching data:', error)
			}
		}

		fetchPosts()
	}

	const dateRangeShortcuts: PickersShortcutsItem<DateRange<dayjs.Dayjs>>[] = React.useMemo(() => getDateRangeShortcuts(t), [t])

	return (
		<ThemeProvider theme={theme}>
			<Paper
				sx={{
					m: 1,
					p: 1,
					display: 'flex',
					flexDirection: 'column',
					overflow: 'hidden',
					border: 'none',
					borderRadius: 0,
					boxShadow: 'none',
				}}
			>
				<Box>
					<Paper
						sx={{
							display: { sm: 'none', xs: 'flex' },
							cursor: 'pointer',
							backgroundColor: '#D9D9D9',
							height: '60px',
							alignItems: 'center',
							justifyContent: 'space-between',
							p: 3,
						}}
						onClick={openSearch}
					>
						<Typography fontSize={'16px'}>Search</Typography>
						<Typography fontSize={'16px'}>{open}</Typography>
					</Paper>
					<Box
						component='form'
						sx={{
							width: '96.5%',
							display: { sm: 'flex', xs: search ? 'none' : 'flex' },
							flexDirection: { sm: 'row', xs: 'column' },
							alignItems: 'flex-end',
						}}
					>
						<LocalizationProvider dateAdapter={AdapterDayjs}>
							<DemoContainer
								components={['DateRangePicker']}
								sx={{
									width: { sm: 0.2, xs: 1 },
									'& .MuiStack-root>:not(style):not(style)': {
										margin: '3px',
									},
									'&.MuiOutlinedInput-notchedOutline': {
										borderColor: 'black',
									},
								}}
							>
								<DateRangePicker
									defaultValue={[dayjs(starting.start), dayjs(starting.end)]}
									onChange={handleDateChange}
									slotProps={{
										// shortcuts: { items: dateRangeShortcuts },
										shortcuts: isSmScreen ? {} : { items: dateRangeShortcuts },
									}}
									sx={{
										'& .MuiInputBase-input': {
											height: '15px',
											fontSize: '16px',
											p: 1.5,
										},

										'& .MuiOutlinedInput-notchedOutline': {
											border: 'none',
											borderRadius: '0px',
											borderBottom: '1px solid #757575',
										},

										'& .Mui-error': {
											borderColor: 'black',
											color: 'black',
										},
										p: 0,
										borderColor: 'black',
									}}
								/>
							</DemoContainer>
							<Autocomplete
								size='small'
								id='combo-box-demo'
								multiple={false}
								options={option.map((id) => ({ label: id.toString() }))}
								getOptionLabel={(option) => option.label}
								isOptionEqualToValue={(option, value) => option.label === value.label}
								onChange={changeOptionValue}
								sx={{
									width: { sm: 420, xs: '100%' },
									pl: { sm: 4.6, xs: '5px' },
									m: { sm: 0, xs: '15px 0px 18px' },
									pr: 0,
									pb: '3px',
								}}
								renderInput={({ InputLabelProps, ...params }: AutocompleteRenderInputParams) => (
									<TextField
										{...params}
										variant='standard'
										InputLabelProps={InputLabelProps as any}
										label={t('nameSurname')}
										sx={{ p: 0, }}
									/>
								)}
							/>
							<Button
								variant='contained'
								onClick={handleSubmit}
								sx={{
									height: 40,
									textTransform: 'capitalize',
									fontSize: 17,
									ml: 5,
									borderRadius: '60px',
									width: { sm: '10%', xs: '100%' },
								}}
								type={'submit'}
							>
								{t('search')}
							</Button>
						</LocalizationProvider>
					</Box>
					<Box></Box>
					<Paper sx={{ border: 'none', borderRadius: '0', boxShadow: 'none', margin: '10px' }}>
						{load ? <Load /> : <AccordionPage fetchData={fetchData} load={load} />}
					</Paper>
				</Box>
			</Paper>
		</ThemeProvider>
	)
}
